import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { encrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import { formatDate } from "../../utils/dateUtils";
import alertService from "../../utils/alertService";
import sellingMasterService from "../../services/sellingMasterService";

function SellingMasterListPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const basePath = useMemo(() => {
    if (location.pathname.includes("/invoice-management/selling-master"))
      return "/invoice-management/selling-master";
    return "/selling-master";
  }, [location.pathname]);

  // --- CRUD Hook ---
  const endpoints = useMemo(
    () => ({
      list: "/selling-master",
      delete: (id) => `/selling-master/${id}`,
    }),
    [],
  );

  const {
    rows,
    isLoading,
    error: listError,
    filters,
    pagination,
    sortConfig,
    askDelete,
    changePage,
    changeSort,
    updateFilters,
    refresh,
  } = useCrud({
    endpoints,
    idKey: "sellingMasterId",
    initialFilters: { search: "" },
    initialSort: { key: "date", order: "DESC" },
  });

  const openCreate = () => navigate(`${basePath}/new`);
  const openEdit = (item) =>
    navigate(`${basePath}/${encrypt(item?.sellingMasterId)}/edit`);

  const handleView = (item) => {
    if (!item?.sellingMasterId) return;
    navigate(`${basePath}/${encrypt(item?.sellingMasterId)}/view`);
  };

  const handleEdit = (item) => {
    if (item?.isReceived) {
      alertService.info(
        "This invoice is already received/locked and cannot be edited. Please unlock it first.",
      );
      return;
    }
    openEdit(item);
  };

  const handleDelete = (item) => {
    if (item?.isReceived) {
      alertService.info(
        "This invoice is already received/locked and cannot be deleted. Please unlock it first.",
      );
      return;
    }
    askDelete(item);
  };

  const handleToggleReceived = async (item) => {
    const isCurrentReceived = Boolean(item?.isReceived);
    const nextStatus = !isCurrentReceived;

    const actionTitle = nextStatus ? "Lock & Complete Invoice" : "Unlock Invoice";
    const message = nextStatus
      ? `Are you sure you want to mark Invoice #${item?.invoiceNo || ""} as Fully Received / Completed?<br/><br/><span class="text-xs text-slate-500">This will lock the invoice from further edits or deletions.</span>`
      : `Are you sure you want to unlock Invoice #${item?.invoiceNo || ""}?<br/><br/><span class="text-xs text-slate-500">This will allow editing and updating the invoice.</span>`;

    const confirm = await alertService.confirm(message, {
      title: actionTitle,
      confirmButtonText: nextStatus ? "Yes, Lock & Complete" : "Yes, Unlock",
    });

    if (!confirm.isConfirmed) return;

    try {
      await sellingMasterService.toggleReceivedStatus(item.sellingMasterId, nextStatus);
      alertService.success(
        nextStatus
          ? "Invoice marked as received and locked successfully."
          : "Invoice unlocked successfully."
      );
      if (typeof refresh === "function") {
        refresh();
      }
    } catch (err) {
      alertService.error(err.message || "Failed to update invoice received status.");
    }
  };

  const tableColumns = useMemo(
    () => [
      {
        key: "date",
        label: "Date",
        sortable: true,
        render: (item) => formatDate(item?.date),
      },
      {
        key: "deliveryDate",
        label: "Delivery Date",
        // sortable: true,
        render: (item) => formatDate(item?.deliveryDate),
      },
      { key: "invoiceNo", label: "Invoice No", sortable: true },
      { key: "customerName", label: "Customer", sortable: true },
      { key: "companyName", label: "Company", sortable: true },
      {
        key: "totalAmount",
        label: "Total Amount",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) =>
          item?.totalAmount
            ? `₹ ${Number(item.totalAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
            : "₹ 0.00",
      },
      {
        key: "grandTotal",
        label: "Grand Total",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) =>
          item?.grandTotal
            ? `₹ ${Number(item.grandTotal).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
            : "₹ 0.00",
      },
      {
        key: "attachment",
        label: "Invoice",
        render: (item) => {
          const fileUrl = item?.attachmentLink || item?.attachmentPath;
          if (!fileUrl) return <span className="text-slate-400">-</span>;
          return (
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-bold text-primary-600 hover:text-primary-700 transition-colors"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414a1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              View
            </a>
          );
        },
      },
      {
        key: "generatedReport",
        label: "Report",
        render: (item) => {
          const fileUrl =
            item?.generatedReportLink || item?.generatedReportPath;
          if (!fileUrl) return <span className="text-slate-400">-</span>;
          return (
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700 transition-all border border-emerald-100 shadow-sm group"
              title="View Generated Report"
            >
              <svg
                className="h-3 w-3 transition-transform group-hover:scale-110"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M11.363 2c4.155 0 2.637 6 2.637 6s6-1.518 6 2.638v11.362c0 .552-.448 1-1 1h-13c-.552 0-1-.448-1-1v-19c0-.552.448-1 1-1h6.363zm.637-2h-8c-1.104 0-2 .896-2 2v20c0 1.104.896 2 2 2h16c1.104 0 2-.896 2-2v-13l-10-9z" />
              </svg>
              <span className="text-[9px] font-black uppercase tracking-tighter">
                REPORT
              </span>
            </a>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        render: (item) => (
          <span
            className={`rounded-full px-2 py-1 text-xs font-semibold ${item?.isActive
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
              : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
              }`}
          >
            {item?.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        key: "isReceived",
        label: "Received",
        headerClassName: "text-center",
        className: "text-center",
        render: (item) => {
          const isReceived = Boolean(item?.isReceived);
          const received = Number(item?.receivedAmount || 0);

          let badgeClass = "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400";
          let dotClass = "bg-rose-500";
          let statusText = "Pending";

          if (isReceived) {
            badgeClass = "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400";
            dotClass = "bg-emerald-500";
            statusText = "Received";
          } else if (received > 0) {
            badgeClass = "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400";
            dotClass = "bg-amber-500";
            statusText = "Partially Received";
          }

          return (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${badgeClass}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
              {statusText}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="space-y-3">
      {listError ? <ErrorAlert message={listError} /> : null}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        toolbarAction={
          <Button
            onClick={openCreate}
            className="shadow-xl shadow-primary-500/10 w-full sm:w-auto justify-center"
          >
            <svg
              className="mr-2 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Create Invoice
          </Button>
        }
        renderActions={(item) => (
          <div className="flex items-center gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              className="px-2.5"
              onClick={() => handleView(item)}
            >
              <svg
                className="mr-1 h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              View
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="px-2.5"
              onClick={() => handleEdit(item)}
              disabled={Boolean(item?.isReceived)}
              title={item?.isReceived ? "Invoice is locked. Unlock to edit" : "Edit Invoice"}
            >
              <svg
                className="mr-1 h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Edit
            </Button>
            <Button
              variant="danger"
              size="sm"
              className="px-2.5"
              onClick={() => handleDelete(item)}
              disabled={Boolean(item?.isReceived)}
              title={item?.isReceived ? "Invoice is locked. Unlock to delete" : "Delete Invoice"}
            >
              <svg
                className="mr-1 h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Delete
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className={`px-2 text-xs font-bold transition-all active:scale-95 ${
                item?.isReceived
                  ? "border-amber-200 text-amber-700 bg-amber-50/50 hover:bg-amber-100 dark:border-amber-900/50 dark:text-amber-400"
                  : "border-emerald-200 text-emerald-700 bg-emerald-50/50 hover:bg-emerald-100 dark:border-emerald-900/50 dark:text-emerald-400"
              }`}
              onClick={() => handleToggleReceived(item)}
              title={item?.isReceived ? "Unlock invoice payment to edit/delete" : "Lock invoice as fully received"}
            >
              <i className="material-icons text-sm">{item?.isReceived ? "lock_open" : "task_alt"}</i>
              {item?.isReceived ? "Unlock" : "Lock"}
            </Button>
          </div>
        )}
        searchKey="search"
        searchPlaceholder="Search by invoice, customer or company"
        tableScrollHeight="58vh"
      />
    </div>
  );
}

export default SellingMasterListPage;
